import { Children, isValidElement, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { iconeModuloDaRota } from "../../layout/iconeModuloRota";
import { normalizarTextoPtBr } from "../../utils/textoPtBr";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { BotaoFechar } from "./BotaoFechar";
import { CardAjudaTela, type CardAjudaTelaProps } from "./CardAjudaTela";
import styles from "./ModalCadastro.module.css";

// Modais sobrepostos compartilham o bloqueio de rolagem até o último fechar.
let modaisAbertos = 0;
let overflowOriginal = "";

export type TipoSecaoModalCadastro =
  | "padrao"
  | "identificacao"
  | "imagem"
  | "complementos"
  | "anexos";

interface ModalCadastroProps {
  aberto: boolean;
  titulo: string;
  subtitulo?: string;
  /** Ícone exibido em destaque à esquerda do título. */
  icone?: ReactNode;
  /** Exibe imagens de identificação em formato circular, sem fundo adicional. */
  iconeCircular?: boolean;
  /** Ações ou metadados exibidos à direita do cabeçalho, antes do botão de fechar. */
  cabecalhoAcoes?: ReactNode;
  onFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
  largura?: number;
  /** Altura desejada em px, limitada à altura disponível da janela. */
  altura?: number;
  painelClassName?: string;
  corpoClassName?: string;
  tourId?: string;
  /** Orientação específica do fluxo. Quando omitida, usa o subtítulo e o texto padrão do modal. */
  orientacao?: Partial<CardAjudaTelaProps>;
  /** Permite ocultar a orientação somente em fluxos que não representam uma tela operacional. */
  exibirOrientacao?: boolean;
}

function contemCardAjuda(conteudo: ReactNode): boolean {
  return Children.toArray(conteudo).some((item) => {
    if (!isValidElement<{ children?: ReactNode }>(item)) return false;
    if (item.type === CardAjudaTela) return true;
    if (item.type === ModalCadastro) return false;
    return contemCardAjuda(item.props.children);
  });
}

/** Modal grande compartilhado para cadastros e edições. */
export function ModalCadastro({
  aberto,
  titulo,
  subtitulo,
  icone,
  iconeCircular = false,
  cabecalhoAcoes,
  onFechar,
  children,
  rodape,
  largura = 1120,
  altura,
  painelClassName,
  corpoClassName,
  tourId,
  orientacao,
  exibirOrientacao = true,
}: ModalCadastroProps) {
  const { traduzir } = useIdioma();
  const overlayRef = useRef<HTMLDivElement>(null);
  /* Sem ícone informado, usa o do módulo em que a tela está — o mesmo do menu
     lateral. Assim todo modal abre identificado pelo seu módulo. */
  const { pathname } = useLocation();
  const IconeModulo = iconeModuloDaRota(pathname);
  const iconeExibido = icone ?? (IconeModulo ? <IconeModulo aria-hidden="true" /> : null);
  const tituloNormalizado = traduzir(normalizarTextoPtBr(titulo));
  const subtituloNormalizado = subtitulo ? traduzir(normalizarTextoPtBr(subtitulo)) : undefined;
  const exibirCardAjuda = exibirOrientacao && !contemCardAjuda(children);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      const overlays = document.querySelectorAll('[data-modal-overlay="true"]');
      // Escape fecha apenas o popup superior, preservando o formulário abaixo.
      if (evento.key === "Escape" && overlays[overlays.length - 1] === overlayRef.current) onFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    if (modaisAbertos === 0) overflowOriginal = document.body.style.overflow;
    modaisAbertos += 1;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      modaisAbertos -= 1;
      if (modaisAbertos === 0) document.body.style.overflow = overflowOriginal;
    };
  }, [aberto, onFechar]);

  if (!aberto) return null;

  return createPortal(
    <div
      ref={overlayRef}
      data-modal-overlay="true"
      className={styles.overlay}
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget) onFechar();
      }}
    >
      <div
        data-vidro="true"
        data-modal-surface="true"
        data-tour={tourId}
        className={[styles.painel, painelClassName].filter(Boolean).join(" ")}
        style={{
          maxWidth: largura,
          ...(altura
            ? {
                height: `min(${altura}px, calc(100vh - 32px))`,
                maxHeight: "calc(100vh - 32px)",
              }
            : null),
        }}
        role="dialog"
        aria-modal="true"
        aria-label={tituloNormalizado}
      >
        <header className={styles.cabecalho}>
          <div className={styles.cabecalhoIdentidade}>
            {iconeExibido && (
              <span
                className={`${styles.iconeModal} ${iconeCircular ? styles.iconeModalCircular : ""}`}
                aria-hidden="true"
              >
                {iconeExibido}
              </span>
            )}
            <div className={styles.tituloWrap}>
              <h2 className={styles.titulo}>{tituloNormalizado}</h2>
              {subtituloNormalizado && <p className={styles.subtitulo}>{subtituloNormalizado}</p>}
            </div>
          </div>
          <div className={styles.cabecalhoLateral}>
            {cabecalhoAcoes ? <div className={styles.cabecalhoAcoes}>{cabecalhoAcoes}</div> : null}
            <BotaoFechar onClick={onFechar} />
          </div>
        </header>

        <div className={[styles.corpo, corpoClassName].filter(Boolean).join(" ")}>
          {exibirCardAjuda && (
            <CardAjudaTela
              paraQueServe={orientacao?.paraQueServe || subtitulo || "Este modal permite consultar ou atualizar as informações apresentadas."}
              comoPreencher={orientacao?.comoPreencher ?? ["Preencha os campos conforme o conteúdo solicitado e revise as informações obrigatórias."]}
              comoFunciona={orientacao?.comoFunciona ?? "Use a ação principal para confirmar; ao cancelar ou fechar, nenhuma nova alteração será aplicada."}
            />
          )}
          {children}
        </div>

        {rodape && <footer className={styles.rodape}>{rodape}</footer>}
      </div>
    </div>,
    document.body
  );
}

interface SecaoModalCadastroProps {
  titulo: string;
  children: ReactNode;
  className?: string;
  tituloClassName?: string;
  tourId?: string;
  tipo?: TipoSecaoModalCadastro;
  exibirTituloTradicional?: boolean;
}

/** Seção compartilhada dos modais de cadastro. */
export function SecaoModalCadastro({
  titulo,
  children,
  className,
  tituloClassName,
  tourId,
  exibirTituloTradicional = true,
}: SecaoModalCadastroProps) {
  const { traduzir } = useIdioma();
  const tituloTraduzido = traduzir(normalizarTextoPtBr(titulo));

  return (
    <section className={className} data-tour={tourId}>
      {exibirTituloTradicional && <h3 className={tituloClassName}>{tituloTraduzido}</h3>}
      {children}
    </section>
  );
}
